import React, { useEffect, useState } from 'react';
import MainLayout from '../../layout/MainLayout';
import { api } from '../../api/axios';
import { FileText, Download } from 'lucide-react';
import { toast } from 'react-toastify';

export default function PushHistory() {
    const [logs, setLogs] = useState([]);
    const [activeLog, setActiveLog] = useState(null);
    const [logContent, setLogContent] = useState('');
    const [loadingList, setLoadingList] = useState(false);
    const [loadingContent, setLoadingContent] = useState(false);

    const fetchList = async () => {
        setLoadingList(true);
        try {
            const res = await api.get('/push-notifications/push-logs');
            setLogs(res.data?.logs || []);
        } catch (err) {
            console.error(err);
            toast.error('Failed to load push logs');
        } finally {
            setLoadingList(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    useEffect(() => {
        if (!activeLog) {
            setLogContent('');
            return;
        }
        (async () => {
            setLoadingContent(true);
            try {
                const res = await api.get(`/push-notifications/push-logs/${activeLog}`);
                // Server returns { content: "..." } based on existing PushNotification.jsx
                setLogContent(res.data?.content ?? '');
            } catch (err) {
                console.error(err);
                toast.error('Failed to load log content');
            } finally {
                setLoadingContent(false);
            }
        })();
    }, [activeLog]);

    const parseCount = (label) => {
        if (!logContent) return null;
        const re = new RegExp(`${label}\\s*[:=]\\s*(\\d+)`, 'i');
        const m = logContent.match(re);
        return m ? Number(m[1]) : null;
    };

    const successCount = parseCount('Success\\s*Count') ?? parseCount('Success');
    const failureCount = parseCount('Failure\\s*Count') ?? parseCount('Failure');

    const handleDownload = () => {
        if (!activeLog || !logContent) {
            toast.info('Select a log first');
            return;
        }
        const blob = new Blob([logContent], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = activeLog.endsWith('.txt') ? activeLog : `${activeLog}.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <MainLayout title="Push Notification History">
            <div className="h-[76dvh] w-full bg-white border rounded-xl shadow flex overflow-hidden">

                {/* LEFT: log list */}
                <div className="w-72 border-r bg-gray-50 p-4 overflow-y-auto">
                    <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                        <FileText size={16} /> Log files
                    </h3>
                    {loadingList ? (
                        <div className="text-xs text-gray-500">Loading...</div>
                    ) : logs.length === 0 ? (
                        <div className="text-xs text-gray-500">No push logs yet.</div>
                    ) : (
                        <ul className="space-y-1 text-sm">
                            {logs.map((log) => (
                                <li
                                    key={log}
                                    onClick={() => setActiveLog(log)}
                                    className={`cursor-pointer p-2 rounded-lg transition ${
                                        activeLog === log ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-200'
                                    }`}
                                >
                                    {log}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* RIGHT: content */}
                <div className="flex-1 p-4 flex flex-col overflow-hidden">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <h3 className="text-sm font-semibold">Details</h3>
                            {activeLog && (
                                <>
                                    {successCount !== null && (
                                        <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700 border border-green-200">
                                            Success: {successCount}
                                        </span>
                                    )}
                                    {failureCount !== null && (
                                        <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 border border-red-200">
                                            Failure: {failureCount}
                                        </span>
                                    )}
                                </>
                            )}
                        </div>
                        <button
                            onClick={handleDownload}
                            disabled={!activeLog || !logContent}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-800 text-white rounded-md disabled:opacity-40"
                        >
                            <Download size={14} /> Download
                        </button>
                    </div>

                    <div className="flex-1 border rounded-lg bg-gray-50 p-4 overflow-auto">
                        {loadingContent ? (
                            <div className="text-xs text-gray-500">Loading content...</div>
                        ) : activeLog ? (
                            <pre className="text-xs whitespace-pre-wrap">{logContent || '(empty log)'}</pre>
                        ) : (
                            <div className="h-full flex items-center justify-center text-sm text-gray-500">
                                Select a log file from the left to view details.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </MainLayout>
    );
}
